import React, { useState, useEffect } from 'react';
import { X, ArrowRightCircle, Calendar, User, Building2, Filter } from 'lucide-react';
import { api } from '../services/api';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface TransferHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Transfer {
  id: string;
  conversationId: string;
  protocolId: string;
  customerName: string | null;
  customerPhone: string;
  fromAgentId: string | null;
  toAgentId: string | null;
  fromDepartmentId: string | null;
  toDepartmentId: string;
  transferType: string;
  transferredAt: string;
  fromAgent: {
    id: string;
    name: string;
    email: string;
  } | null;
  toAgent: {
    id: string;
    name: string;
    email: string;
  } | null;
  fromDepartment: {
    id: string;
    name: string;
  } | null;
  toDepartment: {
    id: string;
    name: string;
  };
}

interface Agent {
  id: string;
  name: string;
  email: string;
}

export const TransferHistoryModal: React.FC<TransferHistoryModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFromAgentId, setSelectedFromAgentId] = useState('');
  const [selectedToAgentId, setSelectedToAgentId] = useState('');
  const [selectedTransferType, setSelectedTransferType] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadAgents();
      loadTransfers();
    }
  }, [isOpen]);

  const loadAgents = async () => {
    try {
      const data = await api.getUsers({});
      setAgents(data.filter(agent => agent.role !== 'ADMIN'));
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const loadTransfers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedFromAgentId) params.fromAgentId = selectedFromAgentId;
      if (selectedToAgentId) params.toAgentId = selectedToAgentId;
      if (selectedTransferType) params.transferType = selectedTransferType;

      // Use a generic request since we haven't added to api.ts yet
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/conversations/transfers/history?${new URLSearchParams(params)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch transfer history');
      }

      const data = await response.json();
      setTransfers(data.transfers);
    } catch (error: any) {
      console.error('Failed to load transfer history:', error);
      setError(error.message || 'Falha ao carregar histórico de transferências');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterApply = () => {
    loadTransfers();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedFromAgentId('');
    setSelectedToAgentId('');
    setSelectedTransferType('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all w-full max-w-6xl z-50 max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <ArrowRightCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Histórico de Transferências
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Visualize todas as transferências de atendimentos
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtros</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Start Date */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Data Início
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Data Fim
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* From Agent */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  De (Agente)
                </label>
                <select
                  value={selectedFromAgentId}
                  onChange={(e) => setSelectedFromAgentId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Todos</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* To Agent */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Para (Agente)
                </label>
                <select
                  value={selectedToAgentId}
                  onChange={(e) => setSelectedToAgentId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Todos</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Transfer Type */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo
                </label>
                <select
                  value={selectedTransferType}
                  onChange={(e) => setSelectedTransferType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Todos</option>
                  <option value="MANUAL">Manual</option>
                  <option value="AUTOMATIC">Automático</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleFilterApply}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white text-sm rounded-lg transition"
              >
                Aplicar Filtros
              </button>
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 text-sm rounded-lg transition"
              >
                Limpar Filtros
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : error ? (
              <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
                {error}
              </div>
            ) : transfers.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <ArrowRightCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Nenhuma transferência encontrada com os filtros selecionados</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transfers.map((transfer) => (
                  <div
                    key={transfer.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow bg-white dark:bg-gray-800"
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Left Side - Transfer Info */}
                      <div className="flex-1 space-y-2">
                        {/* Protocol and Customer */}
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-sm font-mono font-semibold">
                            #{transfer.protocolId}
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {transfer.customerName || transfer.customerPhone}
                          </span>
                          {transfer.transferType === 'AUTOMATIC' ? (
                            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs">
                              Automático
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs">
                              Manual
                            </span>
                          )}
                        </div>

                        {/* Transfer Details */}
                        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                          {/* From */}
                          <div className="flex items-center gap-1">
                            {transfer.fromDepartment && (
                              <>
                                <Building2 className="w-4 h-4" />
                                <span>{transfer.fromDepartment.name}</span>
                              </>
                            )}
                            {transfer.fromAgent && (
                              <>
                                <User className="w-4 h-4 ml-2" />
                                <span>{transfer.fromAgent.name}</span>
                              </>
                            )}
                            {!transfer.fromDepartment && !transfer.fromAgent && (
                              <span className="text-gray-400 italic">Menu</span>
                            )}
                          </div>

                          {/* Arrow */}
                          <ArrowRightCircle className="w-4 h-4 text-purple-500" />

                          {/* To */}
                          <div className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            <span>{transfer.toDepartment.name}</span>
                            {transfer.toAgent && (
                              <>
                                <User className="w-4 h-4 ml-2" />
                                <span>{transfer.toAgent.name}</span>
                              </>
                            )}
                            {!transfer.toAgent && (
                              <span className="text-gray-400 italic ml-2">(Fila)</span>
                            )}
                          </div>
                        </div>

                        {/* Timestamp */}
                        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Calendar className="w-3 h-3" />
                          {format(parseISO(transfer.transferredAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total: {transfers.length} transferência{transfers.length !== 1 ? 's' : ''}
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 dark:bg-gray-600 dark:hover:bg-gray-500 text-white rounded-lg transition"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
